import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, MessagePayload, Messaging, onMessage } from 'firebase/messaging';
import { getFirebaseWebConfig } from '../../utils/env.util';
import { pushService } from './push.service';

const FIREBASE_APP_NAME = 'communicate-pos-web';

export type WebPushRegistrationResult =
  | { status: 'registered'; pushToken: string }
  | { status: 'unavailable'; message: string }
  | { status: 'permission-denied'; message: string };

export type LocalWebNotificationTestResult =
  | { ok: true }
  | { ok: false; message: string };

let foregroundListenerInstalled = false;

const canUseWebPush = (): boolean => (
  typeof window !== 'undefined'
  && 'Notification' in window
  && 'serviceWorker' in navigator
  && window.isSecureContext
);

const getFirebaseApp = (): FirebaseApp | null => {
  const config = getFirebaseWebConfig();
  if (!config) {
    return null;
  }

  return getApps().find((app) => app.name === FIREBASE_APP_NAME)
    ?? initializeApp(config, FIREBASE_APP_NAME);
};

const getFirebaseMessaging = async (): Promise<{ messaging: Messaging; vapidKey: string } | null> => {
  if (!canUseWebPush() || !(await isSupported())) {
    return null;
  }

  const config = getFirebaseWebConfig();
  const app = getFirebaseApp();
  if (!config || !app) {
    return null;
  }

  return { messaging: getMessaging(app), vapidKey: config.vapidKey };
};

const showForegroundNotification = (payload: MessagePayload): void => {
  const title = payload.notification?.title;
  if (!title || Notification.permission !== 'granted') {
    return;
  }

  new Notification(title, {
    body: payload.notification?.body,
    icon: '/logo192.png',
    data: payload.data,
  });
};

export const installFirebaseForegroundMessageListener = async (): Promise<void> => {
  if (foregroundListenerInstalled) {
    return;
  }

  const firebase = await getFirebaseMessaging();
  if (!firebase) {
    return;
  }

  foregroundListenerInstalled = true;
  onMessage(firebase.messaging, showForegroundNotification);
};

export const syncFirebaseWebPushToken = async (tenantId: string): Promise<WebPushRegistrationResult> => {
  if (!canUseWebPush()) {
    return { status: 'unavailable', message: 'התראות פוש אינן נתמכות במכשיר זה.' };
  }
  if (Notification.permission !== 'granted') {
    return { status: 'permission-denied', message: 'לא אושרה הרשאה להתראות.' };
  }

  const firebase = await getFirebaseMessaging();
  if (!firebase) {
    return { status: 'unavailable', message: 'התראות Firebase אינן מוגדרות בסביבה זו.' };
  }

  const registration = await navigator.serviceWorker.ready;
  const pushToken = await getToken(firebase.messaging, {
    vapidKey: firebase.vapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!pushToken) {
    return { status: 'unavailable', message: 'Firebase לא החזיר טוקן התראות.' };
  }

  await pushService.registerToken({ pushToken, platform: 'web', tenantId });
  await installFirebaseForegroundMessageListener();
  return { status: 'registered', pushToken };
};

export const requestAndRegisterFirebaseWebPush = async (tenantId: string): Promise<WebPushRegistrationResult> => {
  if (!canUseWebPush()) {
    return { status: 'unavailable', message: 'התראות פוש אינן נתמכות במכשיר זה.' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { status: 'permission-denied', message: 'לא אושרה הרשאה להתראות.' };
  }

  return syncFirebaseWebPushToken(tenantId);
};

export const triggerLocalWebNotificationTest = async (): Promise<LocalWebNotificationTestResult> => {
  if (!canUseWebPush()) {
    return { ok: false, message: 'המכשיר אינו תומך בהתראות פוש.' };
  }

  if (Notification.permission !== 'granted') {
    return { ok: false, message: 'אין הרשאת התראות בדפדפן.' };
  }

  const registration = await navigator.serviceWorker.ready;
  if (!registration.active) {
    return { ok: false, message: 'Service worker לא פעיל עדיין. נסה לסגור ולפתוח את האפליקציה.' };
  }

  registration.active.postMessage({
    type: 'SHOW_TEST_NOTIFICATION',
    title: 'בדיקת התראה מקומית ✓',
    body: 'הדפדפן וה-service worker יכולים להציג התראות.',
  });

  try {
    new Notification('בדיקת התראה מקומית ✓', {
      body: 'בדיקה ישירה מהדפדפן (foreground).',
      icon: '/logo192.png',
    });
  } catch {
    // Some browsers restrict foreground constructor usage; SW message path remains authoritative.
  }

  return { ok: true };
};